import { keepPreviousData, useQuery } from '@tanstack/react-query';
import React, { FC, useCallback, useMemo, useState } from 'react';

import {
  type BankAccountWithDetails,
  bankAccountGetBankAccountOptions,
  bankAccountListBankAccountsOptions,
} from '@column/api';
import { Dropdown, DropdownOption, Icon, Tooltip, formatNumber } from '@column/column-ui-kit';

import { useDebounce } from '~/hooks/useDebounce';
import { type Action } from '~/lib/authz/eval';
import { FeatureFlag, useFeatureFlag } from '~/lib/flags';
import { getHighlightedField, renderHighlight } from '~/lib/search/highlight';
import { useSessionStore } from '~/stores/Session';

export type BankAccountFilters = Record<string, string | boolean | number | undefined>;

// --- Label components ---

const HighlightedLabel: FC<{ bankAccount: BankAccountWithDetails; accountPreview?: string }> = ({
  bankAccount,
  accountPreview,
}) => {
  const highlightedDisplayName = getHighlightedField(bankAccount.search_metadata, 'DISPLAY_NAME');
  const highlightedDescription = getHighlightedField(bankAccount.search_metadata, 'DESCRIPTION');
  const displayPart = highlightedDisplayName ? renderHighlight(highlightedDisplayName) : bankAccount.display_name;
  const descriptionPart = highlightedDescription ? renderHighlight(highlightedDescription) : bankAccount.description;

  if (accountPreview) {
    return displayPart ? (
      <>
        {displayPart} {accountPreview}
        {descriptionPart ? <> — {descriptionPart}</> : ''}
      </>
    ) : (
      <>
        {descriptionPart || 'Unnamed'} {accountPreview}
      </>
    );
  }

  if (displayPart && descriptionPart) {
    return (
      <>
        {displayPart} – {descriptionPart}
      </>
    );
  }
  return <>{descriptionPart || 'Unnamed'}</>;
};

const PlainLabel: FC<{ displayName?: string; description?: string | null; accountPreview?: string }> = ({
  displayName,
  description,
  accountPreview,
}) => {
  if (accountPreview) {
    return (
      <>
        {displayName
          ? `${displayName} ${accountPreview}${description ? ` — ${description}` : ''}`
          : `${description || 'Unnamed'} ${accountPreview}`}
      </>
    );
  }
  if (displayName && description) return <>{`${displayName} – ${description}`}</>;
  return <>{description || 'Unnamed'}</>;
};

const BankAccountOptionLabel: FC<{
  bankAccount: BankAccountWithDetails;
  highlightSearch: boolean;
  hasSearchQuery: boolean;
}> = ({ bankAccount, highlightSearch, hasSearchQuery }) => {
  const lastFour = bankAccount.default_account_number?.slice(-4);
  const accountPreview = lastFour ? `(...${lastFour})` : undefined;

  if (hasSearchQuery && highlightSearch && bankAccount.search_metadata?.highlights) {
    return <HighlightedLabel bankAccount={bankAccount} accountPreview={accountPreview} />;
  }
  return (
    <PlainLabel
      displayName={bankAccount.display_name}
      description={bankAccount.description}
      accountPreview={accountPreview}
    />
  );
};

// --- Shared hook ---

interface UseBankAccountOptionsConfig {
  value?: string | null;
  filters?: BankAccountFilters;
}

export const useBankAccountOptions = (config: UseBankAccountOptionsConfig = {}) => {
  const { value, filters } = config;
  const isSearchEnabled = useFeatureFlag(FeatureFlag.EnableBankAccountsSearch);
  const [searchText, setSearchText] = useState<string | undefined>();
  const effectiveQuery = useMemo(() => {
    if (isSearchEnabled && searchText) return { ...filters, query: searchText };
    if (!isSearchEnabled && searchText) return { ...filters, description: searchText };
    return filters;
  }, [filters, isSearchEnabled, searchText]);

  const bankAccountsQuery = useQuery({
    ...bankAccountListBankAccountsOptions({ query: effectiveQuery }),
    placeholderData: keepPreviousData,
  });

  const resolvedId = value ?? undefined;

  type ListResponse = { bank_accounts?: BankAccountWithDetails[]; has_more?: boolean } | undefined;
  const bankAccounts = (bankAccountsQuery.data as ListResponse)?.bank_accounts ?? [];

  const activeBankAccountQuery = useQuery({
    ...bankAccountGetBankAccountOptions({ path: { bank_account_id: resolvedId! } }),
    enabled: Boolean(resolvedId),
    placeholderData: () => bankAccounts.find((ba) => ba.id === resolvedId),
  });

  const activeBankAccount = activeBankAccountQuery.data as BankAccountWithDetails | undefined;
  const hasSearchQuery = Boolean(isSearchEnabled && searchText);

  const search = useDebounce((query?: string) => setSearchText(query || undefined), 300, []);

  const toOption = useCallback(
    (ba: BankAccountWithDetails, highlightSearch: boolean): DropdownOption => ({
      label: (
        <BankAccountOptionLabel bankAccount={ba} highlightSearch={highlightSearch} hasSearchQuery={hasSearchQuery} />
      ),
      small: `${formatNumber(ba.balances?.available_amount ?? '')}`,
      value: ba.id,
    }),
    [hasSearchQuery]
  );

  const options: DropdownOption[] = useMemo(
    () => bankAccounts.map((ba) => toOption(ba, true)),
    [bankAccounts, toOption]
  );

  const hiddenOptions = useMemo(() => {
    if (!resolvedId) return [];

    const selected =
      bankAccounts.find((a) => a.id === resolvedId) ||
      (activeBankAccount?.id === resolvedId ? activeBankAccount : undefined);

    return selected ? [toOption(selected, false)] : [];
  }, [activeBankAccount, resolvedId, bankAccounts, toOption]);

  return {
    options,
    hiddenOptions,
    search,
    hasSearchQuery,
    bankAccounts,
  };
};

// --- Component ---

export interface BankAccountDropdownProps {
  value?: string | null;
  onChange: (value: string) => void;
  permission?: Action['permission'];
  unauthorizedText?: string;
  filters?: BankAccountFilters;
  excludeBankAccountIds?: string[];
  fullWidth?: boolean;
  maxWidth?: string;
  hasError?: boolean;
  placeholder?: string;
  isDisabled?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const BankAccountDropdown: FC<BankAccountDropdownProps> = ({
  value,
  onChange,
  permission,
  unauthorizedText,
  filters,
  excludeBankAccountIds,
  fullWidth,
  maxWidth,
  hasError,
  placeholder,
  isDisabled,
  onOpenChange,
}) => {
  const { isAuthorized } = useSessionStore();

  const { options: baseOptions, hiddenOptions: baseHiddenOptions, search } = useBankAccountOptions({ value, filters });

  const excludeSet = useMemo(
    () => (excludeBankAccountIds?.length ? new Set(excludeBankAccountIds) : undefined),
    [excludeBankAccountIds]
  );

  const enrichOption = useCallback(
    (opt: DropdownOption): DropdownOption => {
      if (!permission && !unauthorizedText) return opt;

      const isBankAccountAuthorized = !permission
        ? true
        : isAuthorized({
            permission,
            permissionLevel: 'write',
            bankAccount: { id: opt.value, owners: undefined },
          });

      return {
        ...opt,
        suffix:
          !isBankAccountAuthorized && unauthorizedText ? (
            <Tooltip content={unauthorizedText} placement="right" zIndex={15}>
              <Icon.AnimationLock />
            </Tooltip>
          ) : undefined,
        isDisabled: !isBankAccountAuthorized,
      };
    },
    [isAuthorized, permission, unauthorizedText]
  );

  const options = useMemo(
    () => baseOptions.filter((o) => !excludeSet || !excludeSet.has(o.value)).map(enrichOption),
    [baseOptions, excludeSet, enrichOption]
  );

  const hiddenOptions = useMemo(
    () => baseHiddenOptions.filter((o) => !excludeSet || !excludeSet.has(o.value)).map(enrichOption),
    [baseHiddenOptions, excludeSet, enrichOption]
  );

  return (
    <Dropdown
      options={options}
      hiddenOptions={hiddenOptions}
      active={value ?? undefined}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      emptyMessage="No accounts found"
      onSearchChange={(description: string) => search(description)}
      onChange={onChange}
      onOpenChange={onOpenChange}
      hasError={hasError}
      isDisabled={isDisabled}
      placeholder={placeholder}
      search
    />
  );
};
